import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Heart, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const navItems = [
    { name: 'Início', path: '/' },
    { name: 'Quem Somos', path: '/quem-somos' },
    { name: 'Serviços', path: '/servicos' },
    { name: 'Transparência', path: '/transparencia' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contato', path: '/contato' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-black/[0.04]' : 'bg-white shadow-[0_1px_0_0_rgba(0,0,0,0.05)]'}`}>
      {/* Top Bar */}
      <div className="bg-[#0b3d5c] text-white text-xs hidden md:block">
        <div className="max-w-6xl mx-auto px-6 py-1.5 flex items-center justify-between">
          <span className="text-blue-200/90">Assistência jurídica gratuita em saúde — Goiás</span>
          <a href="tel:08000002560" className="flex items-center gap-1.5 font-semibold hover:text-orange-300 transition-colors">
            <Phone className="h-3 w-3" />
            0800 000 2560 — Atendimento 24h
          </a>
        </div>
      </div>

      {/* Main Nav */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group shrink-0">
            <img src="/logo192.png" alt="Plantão da Saúde" className="h-10 w-10 rounded-xl group-hover:scale-105 transition-transform" width={40} height={40} loading="eager" fetchPriority="high" />
            <div className="flex flex-col leading-tight">
              <span className="text-lg font-bold text-[#0b3d5c] tracking-tight">Plantão da Saúde</span>
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-[0.2em]">IJF-AD</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center">
            <div className="flex items-center gap-0.5">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path}
                  className={`px-3.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                    isActive(item.path)
                      ? 'text-[#0b3d5c] bg-[#0b3d5c]/[0.06] font-semibold'
                      : 'text-gray-500 hover:text-[#0b3d5c] hover:bg-gray-50'
                  }`}>
                  {item.name}
                </Link>
              ))}
            </div>
            <div className="ml-6 pl-6 border-l border-gray-200">
              <Button className="bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-gray-900 font-bold rounded-xl h-9 text-sm px-5 shadow-sm shadow-amber-400/20 transition-all hover:shadow-md hover:-translate-y-px" asChild>
                <Link to="/doacoes"><Heart className="h-3.5 w-3.5 mr-1.5 fill-current" />Apoie</Link>
              </Button>
            </div>
          </nav>

          <button className="lg:hidden p-2 -mr-2 rounded-lg hover:bg-gray-100 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
            {mobileMenuOpen ? <X className="h-5 w-5 text-gray-700" /> : <Menu className="h-5 w-5 text-gray-700" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'max-h-[400px] opacity-100 pb-5' : 'max-h-0 opacity-0'}`}>
          <div className="pt-2 border-t border-gray-100">
            <nav className="flex flex-col gap-0.5">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path) ? 'text-[#0b3d5c] bg-[#0b3d5c]/[0.06]' : 'text-gray-600 hover:bg-gray-50'
                  }`}>{item.name}</Link>
              ))}
              <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-gray-100">
                <Button variant="outline" className="w-full justify-center rounded-xl h-10" asChild>
                  <a href="tel:08000002560"><Phone className="h-4 w-4 mr-2" />0800 000 2560</a>
                </Button>
                <Button className="w-full bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-gray-900 font-bold rounded-xl h-10 shadow-sm" asChild>
                  <Link to="/doacoes"><Heart className="h-4 w-4 mr-2 fill-current" />Apoie Nossa Causa</Link>
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;